/**
 * "Sua revisão fundamentada" sobre um ponto da formalização (E152.F3, ADR-0103 §5.9).
 *
 * As decisões vêm do tipo do ponto, na ordem do servidor. Sem decisão escolhida e sem fundamento
 * escrito, a revisão não sai: checkbox genérico não é juízo especializado. O texto fica no campo
 * até o servidor aceitar; recusa ou falha não o apagam.
 *
 * E168.F5 (AJ-03): a cláusula de autorização localizada (RP-08) pede o documento, a página, a
 * cláusula quando numerada e o trecho; o servidor confere de novo. E172.F6 (B1.2): os campos vêm
 * com a proposta do sistema, e sai o limite de 15 palavras (`ClausulaDaAutorizacao`).
 *
 * E172.F4 (GUS-230 B2.3): na face, com o parecer em vigor lido, a verificação item a item
 * (`VerificacaoPorItem`) vai junto; a revisão só sai com todos os itens decididos.
 */
import { useId, useState, type FormEvent } from 'react';

import { Button, Field, Radio, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ConferenciaFacialParecerData } from '@/lib/conferencia_facial';
import {
  DECISOES_DO_TIPO,
  type ClausulaPeloMotorData,
  type DecisaoDaRevisao,
  type LocalizacaoDaClausulaData,
  type RevisaoInData,
  type TipoDaRevisao,
  type VerificacaoDoItemData,
} from '@/lib/formalizacaoAvaliacao';

import {
  ClausulaDaAutorizacao,
  localInicial,
  localizacaoDe,
  type LocalDaClausula,
} from './ClausulaDaAutorizacao';
import { useVerificacao } from './VerificacaoPorItem';

const t = ptBR.formalizacaoAvaliacao;

/** Só a cláusula localizada leva a localização (e a pede). */
function daDecisao(
  tipo: TipoDaRevisao,
  decisao: DecisaoDaRevisao | null,
  local: LocalDaClausula,
): { pedeLocal: boolean; extra: { localizacao?: LocalizacaoDaClausulaData | null } } {
  if (tipo !== 'autorizacao' || decisao !== 'localizada') return { pedeLocal: false, extra: {} };
  return { pedeLocal: true, extra: { localizacao: localizacaoDe(local) } };
}

/** E172.F4: a verificação item a item vai junto só quando a tela a coletou. */
function comVerificacao(
  revisao: RevisaoInData,
  verificacao: VerificacaoDoItemData[] | undefined,
): RevisaoInData {
  return verificacao ? { ...revisao, verificacao } : revisao;
}

export interface RevisaoFundamentadaProps {
  tipo: TipoDaRevisao;
  objeto: string;
  versao: number;
  enviando: boolean;
  onRegistrar: (revisao: RevisaoInData) => Promise<boolean>;
  onCancelar?: () => void;
  /** E172.F4: o parecer facial em vigor deste ponto, para a verificação item a item. */
  parecerFacial?: ConferenciaFacialParecerData | null;
  /** E172.F6 (B1.2): a cláusula que o sistema localizou, no ponto da autorização. */
  clausula?: ClausulaPeloMotorData | null | undefined;
}

export function RevisaoFundamentada({
  tipo,
  objeto,
  versao,
  enviando,
  onRegistrar,
  onCancelar,
  parecerFacial,
  clausula,
}: RevisaoFundamentadaProps): JSX.Element {
  const [decisao, setDecisao] = useState<DecisaoDaRevisao | null>(null);
  const [fundamento, setFundamento] = useState('');
  const [local, setLocal] = useState<LocalDaClausula>(() => localInicial(clausula));
  const [tentou, setTentou] = useState(false);
  const idDaFalta = useId();
  const verificacao = useVerificacao(tipo === 'face' ? parecerFacial : null, tentou);
  const escrito = fundamento.trim();
  const faltaDecisao = tentou && decisao === null;
  const faltaFundamento = tentou && escrito === '';
  const { pedeLocal, extra } = daDecisao(tipo, decisao, local);

  async function registrar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setTentou(true);
    if (decisao === null || escrito === '' || extra.localizacao === null) return;
    const itens = verificacao.coletada();
    if (itens === null) return;
    const aceita = await onRegistrar(
      comVerificacao({ versao, objeto, decisao, fundamento: escrito, ...extra }, itens),
    );
    if (aceita) {
      setFundamento('');
      setDecisao(null);
      setLocal(localInicial(clausula));
      verificacao.limpar();
      setTentou(false);
    }
  }

  return (
    <form
      className="space-y-sc-3"
      data-testid="revisao-fundamentada"
      data-objeto={objeto}
      noValidate
      onSubmit={(e) => void registrar(e)}
    >
      <fieldset aria-describedby={faltaDecisao ? idDaFalta : undefined}>
        <legend className="mb-[5px] text-caption text-fg-dim">{t.formulario.decisao}</legend>
        <div className="flex flex-col gap-sc-2">
          {DECISOES_DO_TIPO[tipo].map((d) => (
            <Radio
              key={d}
              name={`decisao-${objeto}`}
              value={d}
              checked={decisao === d}
              onChange={() => setDecisao(d)}
              data-decisao={d}
            >
              {t.decisao[d]}
            </Radio>
          ))}
        </div>
        {faltaDecisao ? (
          <p id={idDaFalta} className="mt-1 text-caption text-danger-700">
            {t.formulario.faltaDecisao}
          </p>
        ) : null}
      </fieldset>
      {pedeLocal ? (
        <ClausulaDaAutorizacao
          local={local}
          tentou={tentou}
          clausula={clausula}
          onMudar={setLocal}
        />
      ) : null}
      {verificacao.campos}
      <Field
        label={t.formulario.fundamento}
        description={t.formulario.dica}
        error={faltaFundamento ? t.formulario.faltaFundamento : undefined}
        required
      >
        <Textarea
          value={fundamento}
          maxLength={2000}
          rows={3}
          onChange={(e) => setFundamento(e.target.value)}
        />
      </Field>
      <div className="flex flex-wrap gap-sc-2">
        <Button type="submit" size="sm" disabled={enviando}>
          {enviando ? t.formulario.registrando : t.formulario.registrar}
        </Button>
        {onCancelar ? (
          <Button type="button" variant="secondary" size="sm" onClick={onCancelar}>
            {t.formulario.cancelar}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
